import React from "react";
import { Cloud, Database, Lock, Server } from "lucide-react";

export const About: React.FC = () => {
  return (
    <section
      id="about"
      className="py-20 lg:py-28 bg-cream-100/50 border-b border-charcoal/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Narrative */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-300 text-xs font-mono font-medium text-charcoal-700 uppercase tracking-widest">
              Corporate Background
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
              An Engineering-Led Studio Built in Bengaluru
            </h2>
            <div className="space-y-4 text-base text-ink-secondary leading-relaxed">
              <p>
                <strong className="text-ink font-semibold">Innovriddhi</strong>{" "}
                was founded to solve the persistent operational chaos at the
                boundary between software and physical operations. While
                consumer apps digitized payments and ordering, the core supply
                chain connecting warehouses to regional stockists and field
                sales remained trapped in disconnected spreadsheets and
                unrecorded calls.
              </p>
              <p>
                We do not build generic, one-size-fits-all business software.
                Our product studios create specialized, deeply vertical
                platforms—such as <strong className="text-ink">ivManage</strong>{" "}
                for FMCG distribution and dedicated operating systems for
                field-force intelligence—each engineered with its own domain
                architecture and independent database tier.
              </p>
              <p>
                As the parent company, Innovriddhi provides the shared
                architectural foundation, enterprise cloud infrastructure, and
                rigorous compliance standards that our customers depend on
                daily.
              </p>
            </div>
          </div>

          {/* Right Column: Infrastructure Architecture */}
          <div className="lg:col-span-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-ink-muted mb-4">
              Enterprise Technology Foundation
            </h3>

            {/* Pillar A */}
            <div className="p-6 rounded-2xl bg-white border border-charcoal/10 shadow-sm flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-plum/10 text-plum shrink-0">
                <Cloud className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="font-display text-sm font-bold text-ink">
                  Google Cloud Platform Infrastructure
                </h4>
                <p className="text-xs text-ink-secondary leading-relaxed">
                  Containerized microservices running on Google Cloud Run with
                  VPC peering, auto-scaling under load, and multi-zone failover
                  redundancy in the Asia-Southeast region.
                </p>
              </div>
            </div>

            {/* Pillar B */}
            <div className="p-6 rounded-2xl bg-white border border-charcoal/10 shadow-sm flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-pine/10 text-pine shrink-0">
                <Server className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="font-display text-sm font-bold text-ink">
                  Real-Time Ingestion & Map Matching
                </h4>
                <p className="text-xs text-ink-secondary leading-relaxed">
                  High-concurrency MQTT telemetry brokers paired with custom
                  road-snapped OSRM routing engines, accurately converting noisy
                  GPS traces into verifiable road itineraries.
                </p>
              </div>
            </div>

            {/* Pillar C */}
            <div className="p-6 rounded-2xl bg-white border border-charcoal/10 shadow-sm flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-plum/10 text-plum shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="font-display text-sm font-bold text-ink">
                  Enforced Tenant Isolation
                </h4>
                <p className="text-xs text-ink-secondary leading-relaxed">
                  PostgreSQL database isolation guarantees that customer company
                  datasets are strictly partitioned at the data layer, ensuring
                  zero cross-tenant leakage and audit compliance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
